// SPDX-License-Identifier: LicenseRef-Syncanix-Proprietary
import { Check } from 'lucide-react';

import { activeCardStyle, pillStyle, RESOLVED_TONE, SEVERITY_TONE } from './status-tone';

import type { StatusIncident } from './Status';
import type { StatusSeverity } from './status-tone';
import type { Translator } from '@/i18n/translator';
import type { ReactElement } from 'react';

/** Operator incident text: plain string or per-locale map (EN fallback). */
function localizedIncidentText(
  text: string | Readonly<Record<string, string>> | undefined,
  locale: string,
): string | undefined {
  if (text === undefined || typeof text === 'string') return text;
  return text[locale] ?? text.en ?? Object.values(text)[0];
}

/**
 * One incident.
 *
 * All three severities used to render the same grey pill on the same white
 * card, resolved or not — so an active outage and a maintenance window closed
 * the previous afternoon were visually identical, and the only way to tell them
 * apart was to read four lines of prose. See `status-tone.ts` for why colour
 * here means HAPPENING NOW rather than "was serious".
 */
export function IncidentCard({
  incident,
  t,
  locale,
}: {
  incident: StatusIncident;
  t: Translator;
  locale: string;
}): ReactElement {
  const timeFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
  const title = localizedIncidentText(incident.title, locale) ?? incident.id;
  const body = localizedIncidentText(incident.body, locale);
  const resolved = incident.resolvedAt !== undefined;
  const severity: StatusSeverity = incident.severity;

  return (
    <li
      className={[
        'rounded-lg border border-[color:var(--bd-line)] p-4',
        // The rule is what makes the live ones scannable down the left edge.
        // Logical, so it moves to the right edge under AR/HE.
        resolved ? '' : 'border-s-[3px]',
      ]
        .filter(Boolean)
        .join(' ')}
      style={resolved ? undefined : activeCardStyle(severity)}
    >
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
        <span
          className="rounded-full px-2.5 py-0.5 text-xs font-medium"
          style={
            resolved
              ? // History keeps its word and loses its colour: the severity is
                // still stated, it simply stops competing with what is live.
                {
                  background: 'var(--bd-sunk)',
                  color: 'var(--bd-muted)',
                }
              : pillStyle(SEVERITY_TONE[severity])
          }
        >
          {t(`status.severity.${severity}`)}
        </span>
        {resolved && (
          <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
            style={pillStyle(RESOLVED_TONE)}
          >
            <Check aria-hidden="true" className="size-3.5" strokeWidth={2.5} />
            {t('status.severityResolved')}
          </span>
        )}
        <h3 className="text-sm font-semibold text-[color:var(--bd-ink)]">
          <bdi>{title}</bdi>
        </h3>
      </div>
      {body !== undefined && (
        <p className="mt-2 text-sm text-[color:var(--bd-body)]">
          <bdi>{body}</bdi>
        </p>
      )}
      <p className="mt-2 text-xs text-[color:var(--bd-muted)]">
        {t('status.started', { time: timeFormat.format(new Date(incident.startedAt)) })}
        {incident.resolvedAt !== undefined &&
          ` · ${t('status.resolved', { time: timeFormat.format(new Date(incident.resolvedAt)) })}`}
      </p>
    </li>
  );
}
